import { safeFetch } from "../utils/safeFetch";

const normalizeProductCategoryFields = (product) => {
  const firstCategory = Array.isArray(product?.categories) ? product.categories[0] : null;
  return {
    ...product,
    categoryId: product?.categoryId ?? firstCategory?.categoryId ?? null,
    subcategoryId: product?.subcategoryId ?? firstCategory?.subcategoryId ?? null,
    categoryName: product?.categoryName ?? firstCategory?.category?.name ?? null,
    subcategoryName: product?.subcategoryName ?? firstCategory?.subcategory?.name ?? null,
  };
};

export const getAllProdotti = async (token, tenant, { includeInactive = false } = {}) => {
  const query = includeInactive ? '?includeInactive=true' : '';
  const response = await safeFetch(`/api/products${query}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, includeInactive ? 'allProductsIncludingInactive' : 'allProducts');

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const products = await response.json();
  return Array.isArray(products) ? products.map(normalizeProductCategoryFields) : products;
};

export const getAllProdottiByNow = async (token, tenant) => {
  const response = await safeFetch(`/api/products/by-now`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, 'allProducts');

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const products = await response.json();
  return Array.isArray(products) ? products.map(normalizeProductCategoryFields) : products;
};

export const getProductServiceAvailability = async (token, tenant, { date, eventId } = {}) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (eventId) params.set('eventId', String(eventId));

  const response = await safeFetch(`/api/products/availability?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const creaProdotto = async (prodotto, token, tenant) => {
  const response = await safeFetch(`/api/products/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(prodotto),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const aggiornaProdotto = async (productId, updatedData, token, tenant) => {
  const response = await safeFetch(`/api/products/${productId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(updatedData),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const bulkAssignProductPrinters = async (productIds, printerTargets, token, tenant) => {
  const response = await safeFetch(`/api/products/bulk-printer-assignment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify({ productIds, printerTargets }),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const ordinaProdotti = async (orderedProductIds, token, tenant) => {
  const response = await safeFetch(`/api/products/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(orderedProductIds),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const eliminaProdotto = async (productId, token, tenant) => {
  const response = await safeFetch(`/api/products/${productId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const eliminaProdotti = async (productIds, token, tenant) => {
  const response = await safeFetch('/api/products/bulk-delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify({ productIds }),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error('Impossibile connettersi al server');
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const importaProdottiExcel = async (file, token, tenant) => {
  const fd = new FormData();
  fd.append('file', file);

  const response = await safeFetch(`/api/products/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: fd,
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const creaProdottoMultipart = async (prodotto, file, token, tenant) => {
  // No file → send clean JSON (backend parses it correctly)
  if (!file) {
    const body = {
      name: prodotto.name,
      active: prodotto.active !== false,
      price: prodotto.price,
      availableQuantity: prodotto.availableQuantity ?? null,
      boardBasis: prodotto.boardBasis ?? null,
      pricePerWeight: prodotto.pricePerWeight,
      ...(prodotto.pricePerWeight && prodotto.typeOfWeight ? { typeOfWeight: prodotto.typeOfWeight } : {}),
      categories: prodotto.categories ?? [],
      visibility: prodotto.visibility ?? [],
      printerTargets: prodotto.printerTargets ?? [],
      roomPrinterTargets: prodotto.roomPrinterTargets ?? [],
      ...(Object.prototype.hasOwnProperty.call(prodotto, 'roomIds')
        ? { roomIds: prodotto.roomIds ?? [] }
        : {}),
    };
    if (prodotto.order !== undefined && prodotto.order !== null) {
      body.order = prodotto.order;
    }

    const response = await safeFetch(`/api/products/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        'x-tenant-id': tenant,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 0) throw new Error("Impossibile connettersi al server");
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Has file → send as multipart FormData
  const fd = new FormData();

  fd.append('name', prodotto.name);
  fd.append('active', prodotto.active === false ? 'false' : 'true');
  fd.append('price', String(prodotto.price));
  fd.append('availableQuantity', prodotto.availableQuantity ?? '');
  fd.append('boardBasis', prodotto.boardBasis || '');
  if (prodotto.order !== undefined && prodotto.order !== null) {
    fd.append('order', String(prodotto.order));
  }
  fd.append('pricePerWeight', prodotto.pricePerWeight ? 'true' : 'false');
  if (prodotto.pricePerWeight && prodotto.typeOfWeight) {
    fd.append('typeOfWeight', prodotto.typeOfWeight);
  }
  fd.append('categories', JSON.stringify(prodotto.categories ?? []));
  fd.append('visibility', JSON.stringify(prodotto.visibility ?? []));
  fd.append('printerTargets', JSON.stringify(prodotto.printerTargets ?? []));
  fd.append('roomPrinterTargets', JSON.stringify(prodotto.roomPrinterTargets ?? []));
  if (Object.prototype.hasOwnProperty.call(prodotto, 'roomIds')) {
    fd.append('roomIds', JSON.stringify(prodotto.roomIds ?? []));
  }
  if (file) {
    fd.append('photo', file);
  } else if (prodotto.photoPath) {
    fd.append('photoPath', prodotto.photoPath);
  }

  const response = await safeFetch(`/api/products/create`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: fd,
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const aggiornaProdottoMultipart = async (productId, prodotto, file, token, tenant) => {
  // No file → send clean JSON (backend parses it correctly)
  if (!file) {
    const body = {
      name: prodotto.name,
      active: prodotto.active !== false,
      price: prodotto.price,
      availableQuantity: prodotto.availableQuantity ?? null,
      boardBasis: prodotto.boardBasis ?? null,
      pricePerWeight: prodotto.pricePerWeight,
      ...(prodotto.pricePerWeight && prodotto.typeOfWeight ? { typeOfWeight: prodotto.typeOfWeight } : {}),
      categories: prodotto.categories ?? [],
      visibility: prodotto.visibility ?? [],
      printerTargets: prodotto.printerTargets ?? [],
      roomPrinterTargets: prodotto.roomPrinterTargets ?? [],
      ...(Object.prototype.hasOwnProperty.call(prodotto, 'roomIds')
        ? { roomIds: prodotto.roomIds ?? [] }
        : {}),
      ...(prodotto.photoPath !== undefined ? { photoPath: prodotto.photoPath ?? '' } : {}),
    };
    if (prodotto.order !== undefined && prodotto.order !== null) {
      body.order = prodotto.order;
    }

    const response = await safeFetch(`/api/products/${productId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-tenant-id": tenant,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 0) throw new Error("Impossibile connettersi al server");
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Has file → send as multipart FormData
  const fd = new FormData();

  fd.append('id', String(productId));
  fd.append('name', prodotto.name);
  fd.append('active', prodotto.active === false ? 'false' : 'true');
  fd.append('price', String(prodotto.price));
  fd.append('availableQuantity', prodotto.availableQuantity ?? '');
  fd.append('boardBasis', prodotto.boardBasis || '');
  if (prodotto.order !== undefined && prodotto.order !== null) {
    fd.append('order', String(prodotto.order));
  }
  fd.append('pricePerWeight', prodotto.pricePerWeight ? 'true' : '');
  if (prodotto.pricePerWeight && prodotto.typeOfWeight) {
    fd.append('typeOfWeight', prodotto.typeOfWeight);
  }
  fd.append('categories', JSON.stringify(prodotto.categories ?? []));
  fd.append('visibility', JSON.stringify(prodotto.visibility ?? []));
  fd.append('printerTargets', JSON.stringify(prodotto.printerTargets ?? []));
  fd.append('roomPrinterTargets', JSON.stringify(prodotto.roomPrinterTargets ?? []));
  if (Object.prototype.hasOwnProperty.call(prodotto, 'roomIds')) {
    fd.append('roomIds', JSON.stringify(prodotto.roomIds ?? []));
  }
  fd.append('photo', file);

  const response = await safeFetch(`/api/products/${productId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: fd,
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};
